// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

const NF = new Intl.NumberFormat('en-AU');

export function num(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return NF.format(n);
}

export function pct(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-06-03" → "3 Jun 2026". Input is always an ISO date string or null. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

export function year(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const m = /^(\d{4})/.exec(iso);
  return m ? Number(m[1]) : null;
}

/**
 * A duration in days, said the way a person would say it.
 *
 * Deliberately coarse above a year: "on sale for 4 years" is the honest
 * precision for a range the register records only to the day at each end.
 */
export function duration(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return '—';
  if (days < 0) return '—';
  if (days < 31) return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`;
  if (days < 365) { const m = Math.round(days / 30.44); return `${m} month${m === 1 ? '' : 's'}`; }
  const y = days / 365.25;
  return `${y < 10 ? y.toFixed(1) : Math.round(y)} years`;
}

/**
 * Escape for interpolation into innerHTML.
 *
 * Note the deliberate ORDER of operations wherever this is used: escape FIRST,
 * then truncate, and never the reverse — truncating escaped text can cut an
 * entity in half and emit "&am" into the page.
 */
export function esc(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Truncate on a word boundary, then escape. Never truncate escaped text. */
export function truncEsc(s: string | null | undefined, max: number): string {
  const raw = String(s ?? '');
  if (raw.length <= max) return esc(raw);
  const cut = raw.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${esc(sp > max * 0.6 ? cut.slice(0, sp) : cut)}…`;
}

/**
 * Text for the `data-tip` ATTRIBUTE, set via setAttribute.
 *
 * setAttribute takes RAW newlines — the CSS `white-space: pre-line` on the
 * tooltip renders them. Passing `&#10;` here would print the literal characters
 * "&#10;" to the user; that is only correct when building an HTML string.
 */
export function tip(...lines: (string | null | undefined | false)[]): string {
  return lines.filter(Boolean).join('\n');
}

/** The same tooltip text, for embedding in an innerHTML string. */
export function tipAttr(...lines: (string | null | undefined | false)[]): string {
  return esc(lines.filter(Boolean).join('\n')).replace(/\n/g, '&#10;');
}

export function slugSegment(key: string): string {
  const i = key.lastIndexOf('/');
  return i === -1 ? key : key.slice(i + 1);
}
